import { DRIVE_CONTEXT_KEY, DRIVE_CONTEXT_KEYS } from '@mtm/drive/core';
import { TASK_PANEL_CONTEXT_KEY, VIEW_CONTEXT_KEY } from '../services/view-context-keys';

export interface DriveTaskTarget {
  readonly taskUuid: string;
  readonly boardUuid?: string;
  /** Parent of `taskUuid` when it is a subtask. */
  readonly parentTaskUuid?: string;
}

export interface DriveSelectionTarget {
  /** The folder to open, or the selected file's container. */
  readonly directoryUuid?: string;
  readonly fileAliasUuid?: string;
  /** Feed message to focus once the target opens. */
  readonly messageUuid?: string;
  /** Root of the thread `messageUuid` replies in; absent for a root message. */
  readonly threadRootUuid?: string;
  /** Task to open in the right-panel task sidebar once the target opens. */
  readonly task?: DriveTaskTarget;
}

/**
 * Builds a detached Drive address that opens `target`, keeping every context
 * key the host owns and dropping whatever Drive selection the current page has.
 * The right panel (`ts`/`cm`) and the task panel's open task are dropped too:
 * they belong to the page the link was made on, and a link that still asked for
 * tasks would fight the feed the target opens. A `task` target reopens the task
 * panel on that task instead. The shared view context (`?ctx=`) is the only
 * Drive address; `?dirId` and `?fileId` are not read by the explorer. A file
 * target should carry its container too, as the explorer's own minted
 * addresses do.
 */
export function driveSelectionContextState(
  currentState: Readonly<Record<string, string | boolean>>,
  target: DriveSelectionTarget
): Record<string, string | boolean> {
  const droppedKeys: ReadonlySet<string> = new Set([
    ...DRIVE_CONTEXT_KEYS.map((descriptor: { readonly key: string }): string => descriptor.key),
    ...Object.values(TASK_PANEL_CONTEXT_KEY),
    VIEW_CONTEXT_KEY.task,
    VIEW_CONTEXT_KEY.comment,
  ]);
  const state: Record<string, string | boolean> = Object.fromEntries(
    Object.entries(currentState).filter(
      (contextEntry: [string, string | boolean]): boolean => !droppedKeys.has(contextEntry[0])
    )
  );
  if (target.directoryUuid) state[DRIVE_CONTEXT_KEY.selectedDirectory] = target.directoryUuid;
  if (target.fileAliasUuid) state[DRIVE_CONTEXT_KEY.selectedFile] = target.fileAliasUuid;
  // Same address Drive's own conversation search writes to land on a message.
  if (target.messageUuid) {
    state[DRIVE_CONTEXT_KEY.sidebarTab] = 'comments';
    state[DRIVE_CONTEXT_KEY.focusedMessage] = target.messageUuid;
    if (target.threadRootUuid) state[DRIVE_CONTEXT_KEY.threadRoot] = target.threadRootUuid;
  }
  // Same keys the task panel writes for an open task, so it restores on load.
  if (target.task) {
    const { taskUuid, boardUuid, parentTaskUuid }: DriveTaskTarget = target.task;
    state[VIEW_CONTEXT_KEY.task] = true;
    state[TASK_PANEL_CONTEXT_KEY.taskId] = taskUuid;
    if (boardUuid) state[TASK_PANEL_CONTEXT_KEY.boardId] = boardUuid;
    if (parentTaskUuid) state[TASK_PANEL_CONTEXT_KEY.parentTaskId] = parentTaskUuid;
  }
  return state;
}
